import React, { useEffect, useReducer, useRef, useState } from 'react';

import ProgressIcon from 'icons/progress.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import locationSelectorReducer from 'reducers/domain/simpleCollectionReducer';

import setupPolling from 'utilities/setupPolling';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';
import PendingBars from 'components/application/PendingBars';
import SelectionRow from 'components/application/SelectionRow';
import Tooltip from 'components/application/Tooltip';

import { useBreadBoard } from 'components/contexts/Toaster';

import {
  findRelationalHasManyRelations,
  resourcesAsDomainData,
} from 'components/helpers/storeHelpers';

import * as procoreAPI from '../../requests';
import { destinationShape } from '../../Shapes';
import * as ConfirmationModal from './Confirmation';
import * as SelectionModal from './Selection';

export const initialResourceState = resourcesAsDomainData([
  'company',
  'project',
  'folder',
]);

const folderAncestorIds = ({
  ancestorCollection = [],
  folderID,
  folders,
  startFolderID,
}) => {
  const folder = folders.find(
    (folderIteration) => folderIteration.id === folderID,
  );
  const parent = folder.relationships.parent.data;
  if (parent) {
    if (folder.id !== startFolderID) {
      ancestorCollection.unshift(folder.id);
    }
    folderAncestorIds({
      startFolderID,
      folderID: parent.id,
      folders,
      ancestorCollection,
    });
  }
  return ancestorCollection;
};

const defaultInteractions = {
  searchQuery: '',
  selectedCompanyID: null,
  selectedProjectID: null,
  selectedFolderID: null,
  traversedProjectID: null,
  traversedFolderIDs: [],
};

const pollingInterval = 1000;
const pollingMaxTime = 600000;

export default function Modal({
  isSelecting,
  onCancel,
  onSelection,
  seededLocation,
}) {
  const breadBoard = useBreadBoard();

  const bouncerPolling = useRef(null);
  const companiesPolling = useRef(null);
  const foldersPolling = useRef(null);
  const projectsPolling = useRef(null);

  const [interactions, setInteractions] = useState(defaultInteractions);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingProjectDetails, setIsFetchingProjectDetails] =
    useState(false);
  const [isInitialised, setIsInitialised] = useState(false);

  const [locationSelectorStore, dispatch] = useReducer(
    locationSelectorReducer,
    { domain: initialResourceState },
  );

  const [projectPermissions, setProjectPermissions] = useState({});

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'procore.fileDestination.components.modal.Modal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  // Computed values
  const {
    searchQuery,
    selectedCompanyID,
    selectedFolderID,
    selectedProjectID,
    traversedFolderIDs,
    traversedProjectID,
  } = interactions;

  // -- company resources
  const selectedCompany =
    selectedCompanyID &&
    locationSelectorStore.domain.companyCollection.data[selectedCompanyID];

  // -- project resources
  const selectedProject =
    selectedProjectID &&
    locationSelectorStore.domain.projectCollection.data[selectedProjectID];
  const traversedProject =
    traversedProjectID &&
    locationSelectorStore.domain.projectCollection.data[traversedProjectID];

  // -- folder resources
  const selectedFolder =
    locationSelectorStore.domain.folderCollection.data[selectedFolderID] ||
    (selectedProject && getSelectedProjectRootFolder());
  const traversedFolders = traversedFolderIDs.map(
    (folderID) => locationSelectorStore.domain.folderCollection.data[folderID],
  );
  const latestTraversedFolder =
    traversedFolders.length > 0 &&
    traversedFolders[traversedFolders.length - 1];

  // -- computed
  // TODO figure out safe navigation type behaviour
  const isCompanyProjectCollectionLoaded = !!(
    selectedCompany &&
    selectedCompany.relationships &&
    selectedCompany.relationships.projects &&
    selectedCompany.relationships.projects.data
  );
  const isPending =
    isFetching ||
    !isInitialised ||
    (!!traversedProject && isFetchingProjectDetails);
  const isSelectedProjectFolderCollectionLoaded = !!(
    selectedProject &&
    selectedProject.relationships &&
    selectedProject.relationships.folders &&
    selectedProject.relationships.folders.data
  );

  const destinationBlocker = (() => {
    switch (true) {
      case isPending || isFetchingProjectDetails:
        return 'pending';
      case !selectedProject:
        return 'noProject';
      case !projectPermissions[selectedProjectID]:
        return 'noProjectPermission';
    }
  })();

  const companyCount =
    locationSelectorStore.domain.companyCollection.allDataIds.length;

  const displayableCompanies = () =>
    locationSelectorStore.domain.companyCollection.allDataIds.map(
      (id) => locationSelectorStore.domain.companyCollection.data[id],
    );

  // SELECTORS
  const displayableProjects = () =>
    isCompanyProjectCollectionLoaded ?
      findRelationalHasManyRelations(
        locationSelectorStore,
        selectedCompany,
        'project',
      )
    : [];

  const displayableFolders = () => {
    if (!isSelectedProjectFolderCollectionLoaded) {
      return [];
    }
    const parentFolder =
      latestTraversedFolder ||
      findRelationalHasManyRelations(
        locationSelectorStore,
        selectedProject,
        'folder',
      ).find((folder) => !folder.relationships.parent.data);
    return findRelationalHasManyRelations(
      locationSelectorStore,
      parentFolder,
      'folder',
    );
  };

  const selectionLevel = (() => {
    if (!selectedCompany) return 'company';
    if (!traversedProject) return 'project';
    return 'folder';
  })();

  const searchedResources = (resources) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return resources;

    return resources.filter((resource) =>
      resource.attributes.name.toLowerCase().includes(query),
    );
  };

  const displayableResources = () => {
    switch (selectionLevel) {
      case 'company':
        return displayableCompanies();
      case 'project':
        return searchedResources(displayableProjects());
      default:
        return searchedResources(displayableFolders());
    }
  };

  function getSelectedProjectRootFolder() {
    if (!selectedProject.relationships) return null;
    const rootRelationship = selectedProject.relationships.folders.data.find(
      (folder) =>
        !locationSelectorStore.domain.folderCollection.data[folder.id]
          .relationships.parent.data,
    );
    return locationSelectorStore.domain.folderCollection.data[
      rootRelationship.id
    ];
  }

  function fetchDestination() {
    return Promise.all([
      startCompaniesPolling(),
      startProjectsPolling({ companyID: seededLocation.companyID }),
      startFoldersPolling({
        companyID: seededLocation.companyID,
        projectID: seededLocation.projectID,
      }),
    ]);
  }

  const handlePollingResponseCheck = (response) =>
    ['completed', 'failed'].includes(response.data.meta.requestStatus);

  const startPolling = ({ polling, requestFn }) => {
    polling.current = setupPolling({
      requestFn,
      testFn: handlePollingResponseCheck,
      intervalTime: pollingInterval,
      maxTime: pollingMaxTime,
    });

    return polling.current.startPolling();
  };

  const startBouncerPolling = () =>
    startPolling({
      polling: bouncerPolling,
      requestFn: () =>
        procoreAPI.fetchProcorePermissions({
          companyID: selectedCompanyID,
          projectID: selectedProjectID,
        }),
    });

  const startCompaniesPolling = () =>
    startPolling({
      polling: companiesPolling,
      requestFn: () => procoreAPI.fetchProcoreCompanies(),
    });

  const startFoldersPolling = ({ companyID, projectID }) =>
    startPolling({
      polling: foldersPolling,
      requestFn: () =>
        procoreAPI.fetchProcoreDocuments({ companyID, projectID }),
    });

  const startProjectsPolling = ({ companyID }) =>
    startPolling({
      polling: projectsPolling,
      requestFn: () => procoreAPI.fetchProcoreCompanyProjects({ companyID }),
    });

  // EVENT HANDLERS
  const handleCompanySelection = (companyID) => {
    setInteractions({
      ...interactions,
      searchQuery: '',
      selectedCompanyID: companyID,
      selectedProjectID: null,
      selectedFolderID: null,
      traversedProjectID: null,
      traversedFolderIDs: [],
    });
  };

  const handleProjectSelection = (projectID) =>
    setInteractions({ ...interactions, selectedProjectID: projectID });
  const handleFolderSelection = (folderID) =>
    setInteractions({ ...interactions, selectedFolderID: folderID });
  const handleTraversalReset = () => handleCompanySelection(null);
  const handleProjectTraversal = (projectID) =>
    setInteractions({
      ...interactions,
      searchQuery: '',
      selectedProjectID: projectID,
      traversedProjectID: projectID,
      traversedFolderID: null,
      traversedFolderIDs: [],
    });
  const handleFolderTraversed = (folderID) =>
    setInteractions({
      ...interactions,
      searchQuery: '',
      traversedFolderIDs: [...interactions.traversedFolderIDs, folderID],
    });
  const handleFolderTraversalTruncation = (index) =>
    setInteractions({
      ...interactions,
      searchQuery: '',
      traversedFolderIDs: interactions.traversedFolderIDs.slice(0, index + 1),
    });
  const handleSearchQueryChange = (event) =>
    setInteractions({ ...interactions, searchQuery: event.target.value });
  const handleSearchQueryReset = () =>
    setInteractions({ ...interactions, searchQuery: '' });
  const handleDestinationSelection = () => setIsConfirming(true);
  const handleConfirmationCancel = () => setIsConfirming(false);

  const handleDestinationConfirmation = () => {
    setIsConfirming(false);
    onSelection({
      company: selectedCompany,
      project: selectedProject,
      folder: selectedFolder,
    });
  };

  const handleDestinationFetched = (responses) => {
    const [companiesResponse, projectsResponse, foldersResponse] = responses;

    dispatchResourcesFetched([
      { resources: companiesResponse.data.data },
      {
        resourceType: 'projects',
        resources: projectsResponse.data.data,
        relationship: {
          name: 'projects',
          resource: { id: seededLocation.companyID, type: 'company' },
        },
      },
      {
        resourceType: 'folders',
        resources: foldersResponse.data.data,
        relationship: {
          name: 'folders',
          resource: { id: seededLocation.projectID, type: 'project' },
        },
      },
    ]);

    setInteractions({
      ...interactions,
      selectedCompanyID: seededLocation.companyID,
      selectedFolderID: seededLocation.folderID,
      selectedProjectID: seededLocation.projectID,
      traversedFolderIDs: folderAncestorIds({
        folderID: seededLocation.folderID,
        folders: foldersResponse.data.data,
        startFolderID: seededLocation.folderID,
      }),
      traversedProjectID: seededLocation.projectID,
    });

    // Note that this assumes that permissions check has been done as part of seed lookup
    setProjectPermissions({ [seededLocation.projectID]: true });
  };

  const dispatchResourcesFetched = (payload) => {
    dispatch({ type: 'RESOURCES_FETCHED', payload });
  };

  const endPolling = (polling) => {
    if (polling.current) {
      polling.current.endPolling();
      polling.current = null;
    }
  };

  const isResponseCompleted = (response) =>
    response.data.meta.requestStatus === 'completed';

  useEffect(() => {
    let fetchPromise;

    if (isSelecting && !isInitialised) {
      if (seededLocation) {
        fetchPromise = () => fetchDestination().then(handleDestinationFetched);
      } else {
        fetchPromise = () =>
          startCompaniesPolling().then((response) => {
            if (isResponseCompleted(response)) {
              dispatchResourcesFetched({ resources: response.data.data });
            }
          });
      }

      fetchPromise()
        .catch(breadBoard.addInedibleToast)
        .finally(() => setIsInitialised(true));
    }
  }, [isSelecting]);

  // selected company if only one
  useEffect(() => {
    if (companyCount === 1 && !selectedCompanyID && isInitialised) {
      handleCompanySelection(
        locationSelectorStore.domain.companyCollection.allDataIds[0],
      );
    }
  }, [companyCount, selectedCompanyID, isInitialised]);

  // load projects if not loaded
  useEffect(() => {
    if (
      selectedCompanyID &&
      !locationSelectorStore.domain.companyCollection.data[selectedCompanyID]
        .relationships
    ) {
      setIsFetching(true);

      startProjectsPolling({ companyID: selectedCompanyID })
        .then((response) => {
          if (isResponseCompleted(response)) {
            dispatchResourcesFetched({
              resourceType: 'projects',
              resources: response.data.data,
              relationship: {
                name: 'projects',
                resource: { id: selectedCompanyID, type: 'company' },
              },
            });
          }
        })
        .finally(() => setIsFetching(false));
    }
  }, [selectedCompanyID]);

  // TODO verify if we want this behaviour - will force lookup with current approach of useEffect instead move to event?
  // useEffect(() => {
  //   // select first project
  //   if (!selectedProjectID && displayableProjects().length > 0) {
  //     setInteractions({...interactions, selectedProjectID: displayableProjects()[0].id })
  //   }
  // })

  // load folders if not loaded
  useEffect(() => {
    if (
      selectedProjectID &&
      !locationSelectorStore.domain.projectCollection.data[selectedProjectID]
        .relationships
    ) {
      setIsFetchingProjectDetails(true);

      Promise.all([
        startBouncerPolling(),
        startFoldersPolling({
          companyID: selectedCompanyID,
          projectID: selectedProjectID,
        }),
      ])
        .then((responses) => {
          const [bouncerResponse, foldersResponse] = responses;

          if (isResponseCompleted(bouncerResponse)) {
            setProjectPermissions({
              ...projectPermissions,
              [selectedProjectID]:
                bouncerResponse.data.data.attributes.canManageFolders,
            });
          }

          if (isResponseCompleted(foldersResponse)) {
            dispatchResourcesFetched({
              resourceType: 'folders',
              resources: foldersResponse.data.data,
              relationship: {
                name: 'folders',
                resource: { id: selectedProjectID, type: 'project' },
              },
            });
          }
        })
        .finally(() => setIsFetchingProjectDetails(false));
    }
  }, [selectedProjectID]);

  useEffect(
    () => () => {
      endPolling(bouncerPolling);
      endPolling(companiesPolling);
      endPolling(foldersPolling);
      endPolling(projectsPolling);
    },
    [],
  );

  function breadCrumbOptions() {
    const breadCrumbs = [
      {
        children:
          selectedCompany ? selectedCompany.attributes.name
          : isPending ? i18nTShared('loading...')
          : i18nT('modalContent.breadcrumbs.selectCompany'),
        onClick:
          selectedCompany ?
            () => handleCompanySelection(selectedCompanyID)
          : undefined,
      },
    ];
    if (traversedProject) {
      breadCrumbs.push({
        children: traversedProject.attributes.name,
        onClick: () => handleFolderTraversalTruncation(-1),
      });
    }
    traversedFolders.forEach((traversedFolder, i) => {
      breadCrumbs.push({
        children: traversedFolder.attributes.name,
        onClick: () => handleFolderTraversalTruncation(i),
      });
    });
    return breadCrumbs;
  }

  // -- render options

  if (!isSelecting) return null;

  const isSearchable = selectionLevel !== 'company';
  const displayedResources = isPending ? [] : displayableResources();
  const hasNoSearchResults =
    isSearchable && !isPending && displayedResources.length === 0;
  const resourceCount =
    isSearchable &&
    !isPending &&
    i18nT(`search.${selectionLevel}Count`, {
      count: displayedResources.length,
    });
  const searchPlaceholder = (() => {
    if (!isSearchable) return null;

    if (latestTraversedFolder) {
      return i18nT('search.placeholder.inFolder', {
        folderName: latestTraversedFolder.attributes.name,
        interpolation: { escapeValue: false },
      });
    }

    if (selectionLevel === 'folder' && traversedProject) {
      return i18nT('search.placeholder.folder', {
        projectName: traversedProject.attributes.name,
        interpolation: { escapeValue: false },
      });
    }

    return i18nT(`search.placeholder.${selectionLevel}`);
  })();

  const selectionOptions = () => {
    switch (true) {
      case isPending:
        return <PendingBars />;
      case selectionLevel === 'company':
        return displayedResources.map((company) => (
          <SelectionRow
            content={company.attributes.name}
            isSelected={selectedCompanyID === company.id}
            key={`companySelectionOption--${company.id}`}
            onSelection={() => handleCompanySelection(company.id)}
            showTraverse={true}
          />
        ));
      case selectionLevel === 'project':
        return displayedResources.map((project) => (
          <SelectionRow
            content={project.attributes.name}
            isSelected={selectedProjectID === project.id}
            key={`projectSelectionOption--${project.id}`}
            onSelection={() => handleProjectSelection(project.id)}
            onTraversal={() => handleProjectTraversal(project.id)}
            showTraverse={true}
          />
        ));
      default:
        return displayedResources.map((folder) => (
          <SelectionRow
            childCount={folder.relationships.folders.data.length}
            content={folder.attributes.name}
            isSelected={selectedFolderID === folder.id}
            key={`folderSelectionOption--${folder.id}`}
            onSelection={() => handleFolderSelection(folder.id)}
            onTraversal={() => handleFolderTraversed(folder.id)}
            showTraverse={folder.relationships.folders.data.length > 0}
          />
        ));
    }
  };

  const modalContent = (
    <div>
      {isConfirming ?
        <ConfirmationModal.Content
          folderName={selectedFolder.attributes.name}
        />
      : <SelectionModal.Content
          breadCrumbOptions={breadCrumbOptions()}
          hasNoSearchResults={hasNoSearchResults}
          isSearchable={isSearchable}
          onSearchQueryChange={handleSearchQueryChange}
          onSearchQueryReset={handleSearchQueryReset}
          onTraversalReset={handleTraversalReset}
          resourceCount={resourceCount}
          searchPlaceholder={searchPlaceholder}
          searchQuery={searchQuery}
          selectionOptions={selectionOptions()}
        />
      }
    </div>
  );

  const footer = (
    <div className='modalbox__footer-actions modalbox__footer-actions--right'>
      <TextButton
        className='tw-mr-3'
        color='blue'
        onClick={isConfirming ? handleConfirmationCancel : onCancel}
        size='md'
      >
        {isConfirming ? i18nTShared('back') : i18nTShared('cancel')}
      </TextButton>
      <FilledButton
        className={destinationBlocker ? 'tooltip-parent--disallowed' : ''}
        color='mint'
        disabled={!!destinationBlocker}
        onClick={
          destinationBlocker ? () => {}
          : isConfirming ?
            handleDestinationConfirmation
          : handleDestinationSelection
        }
      >
        <span className='tw-flex tw-w-5.5 tw-shrink-0 tw-items-center'>
          {isFetchingProjectDetails && <ProgressIcon height={18} width={18} />}
        </span>
        {isConfirming ?
          i18nTShared('confirm')
        : destinationBlocker ?
          <Tooltip
            placement='top'
            tooltip={
              destinationBlocker === 'pending' ?
                i18nTShared('loading...')
              : i18nT(`footer.blockerTooltip.${destinationBlocker}`)
            }
            trigger='hover'
          >
            {i18nT('footer.button')}
          </Tooltip>
        : i18nT('footer.button')}
        <span className='tw-w-5.5 tw-shrink-0' />
      </FilledButton>
    </div>
  );

  return (
    <ModalBox
      customFooter={footer}
      isOpen={isSelecting}
      mode='flexible'
      onClose={() => {}}
    >
      {modalContent}
    </ModalBox>
  );
}

Modal.propTypes = {
  isSelecting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSelection: PropTypes.func.isRequired,
  seededLocation: destinationShape,
};
